import dayjs from 'dayjs';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { InputSwitch } from 'primereact/inputswitch';
import { Tooltip } from 'primereact/tooltip';
import { components, MultiValueGenericProps, OptionProps } from 'react-select';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '@/config/i18n';
import { apiRequest } from '@/services';
import { MentorFormType, mentorFormSchema } from '@/shared/schema/mentor';
import { InputTextCustomForm } from '@/components/common/ui/input/Input';
import ButtonCustom from '@/components/common/ui/button';
import { CalendarCustomForm } from '@/components/common/ui/calendar';
import { TextareaCustomForm } from '@/components/common/ui/textarea';
import UploadImage from '@/components/common/ui/upload/uploadImage';
import { useSelectorUserInfo } from '@/components/auth/AuthContext';
import { getValueFromSelect, toastErrorMessage, toastMessage } from '@/lib/utils';
import PATH from '@/routes/path';
import { HTTP_STATUS, MAX_LENGTH } from '@/shared/constants';
import { ASSIGN_COACH_ERROR_CODE, ROLE } from '@/shared/constants/user';
import { useLoading } from '@/components/common/loader/LoadingContext';
import { ToastContext } from '@/components/common/CommonToast';
import useScrollToError from '@/hooks/useScrollToError';
import InfinitySelect from '../common/ui/select';
import { OptionType } from '@/types/api';
import { AssignedCoach } from '@/types/user';

type Props = {
  id?: number;
};

/**
 * Selected 担当コーチ tags only render `label` (full_name_kanji) - names can collide, so add an
 * email tooltip on hover. InfinitySelect itself is shared by other screens and does not override
 * `MultiValueLabel`, so this is passed in locally via `components` on the coach_ids field below
 * rather than changed in InfinitySelect - scoped to this form only.
 */
const CoachMultiValueLabel: React.FC<MultiValueGenericProps<OptionType>> = (props) => {
  const { label, subLabel, value } = props.data;
  return (
    <components.MultiValueLabel {...props}>
      <Tooltip target={`.tooltip-coach-${value}`}>{subLabel}</Tooltip>
      <span className={`tooltip-coach-${value}`} data-pr-position="top">
        {label}
      </span>
    </components.MultiValueLabel>
  );
};

/**
 * Replicates InfinitySelect's own subLabel dropdown row (not exported from there), since passing
 * a local `components` prop below replaces its whole `components` object instead of merging into
 * it - without this, opening the picker would lose the existing email-under-name list rendering.
 */
const CoachOption: React.FC<OptionProps<OptionType>> = (props) => {
  const { label, subLabel } = props.data;
  return (
    <components.Option {...props}>
      <span>{label}</span>
      <span
        style={{ color: 'darkgray' }}
        className="block whitespace-nowrap overflow-hidden text-ellipsis"
      >
        {subLabel}
      </span>
    </components.Option>
  );
};

const MentorForm: React.FC<Props> = ({ id }) => {
  const { toast } = useContext(ToastContext);
  const [t] = useTranslation('');
  const { apiService } = apiRequest();
  const [isActive, setIsActive] = useState(true);
  const [defaultImage, setDefaultImage] = useState<string | undefined>();
  const [assignedCoachOptions, setAssignedCoachOptions] = useState<OptionType[]>(
    [],
  );

  const navigate = useNavigate();
  const { scrollToError } = useScrollToError();
  const { loadingGlobal, setLoadingGlobal } = useLoading();
  const { currentUser } = useSelectorUserInfo();
  const userRole = currentUser?.current_roles?.[0] || '';
  const canPickCoach = [ROLE.ADMIN, ROLE.SUPPER_ADMIN].includes(userRole);

  // Snapshot of the coaches this mentor had when the form was loaded.
  // `coach_ids` replaces the whole list server-side, so it is only sent when
  // the user actually touched the picker. Stays null on the create form.
  const initialCoachIds = useRef<string[] | null>(null);

  const formMethods = useForm<MentorFormType>({
    mode: 'onChange',
    resolver: yupResolver(mentorFormSchema),
    shouldFocusError: false,
    defaultValues: {
      require_password_update: true,
      status: true,
      email: '',
      password: '',
      password_confirmation: '',
      first_name_kanji: '',
      last_name_kanji: '',
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: '',
      phone_number: '',
      post_code: '',
      address: '',
      coach_ids: [],
    },
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    setError,
    formState: { errors },
  } = formMethods;

  // Coaches that can still be picked: active, and not supervised by anyone yet.
  // Coaches already supervised by this mentor are NOT returned here — they come
  // from the detail response and are merged in through `defaultOption`.
  const fetchCoachOptions = useCallback(async (params: any) => {
    return await apiService.users.index({
      ...params,
      assignable_coach: 1,
    });
  }, []);

  function toCoachOption(coach: AssignedCoach): OptionType {
    // A locked coach keeps its link, so it may show up here. The picker itself
    // never returns locked accounts, hence the label is only needed on this side.
    //
    // The suffix goes on `label`, not `subLabel`: the tag's visible text is `label`
    // (CoachMultiValueLabel above) - `subLabel` only surfaces as the hover tooltip
    // and the dropdown list row. Putting the status on subLabel would hide it on
    // the tag, only visible on hover.
    const isInactive = coach.status !== 'active';
    return {
      label: isInactive
        ? `${coach.full_name_kanji}${t('user.assigned_coach_inactive_suffix')}`
        : coach.full_name_kanji,
      subLabel: coach.email,
      value: String(coach.id),
    };
  }

  const getAllFieldNames = () => {
    const values = getValues();
    return Object.keys(values);
  };

  function setDefaultValue(data: any) {
    const specialField = [
      'gender',
      'date_of_birth',
      'status',
      'require_password_update',
      'coach_ids',
    ];
    getAllFieldNames().forEach((field: any) => {
      const val = data[field];

      if (val !== undefined && val !== null && !specialField.includes(field)) {
        setValue(field, val);
      }

      if (field === 'gender') {
        const gender = data.gender ?? 'male';
        setValue(field, { label: t(`common.gender.${gender}`), value: gender } as any);
      }

      if (field === 'date_of_birth') {
        if (data.date_of_birth) {
          setValue(field, new Date(data.date_of_birth));
        }
      }

      if (field === 'status') {
        const status = data.status === 'active';
        setValue(field, status);
        setIsActive(status);
      }

      if (field === 'require_password_update') {
        setValue(field, false);
      }

      if (field === 'coach_ids') {
        const options = ((data.coaches ?? []) as AssignedCoach[]).map(
          toCoachOption,
        );
        setValue(field, options as any);
        setAssignedCoachOptions(options);
        initialCoachIds.current = options.map((option) => String(option.value));
      }
    });
  }

  async function getMentorDetail(mentorId: number) {
    setLoadingGlobal(true);
    const { data: mentor } = await apiService.users.show(mentorId);

    if (mentor?.data) {
      setDefaultValue(mentor.data);
      setDefaultImage(mentor.data.profile_image);
    }
    setLoadingGlobal(false);
  }

  useEffect(() => {
    if (!id) {
      setValue('gender', {
        label: t('common.gender.male'),
        value: 'male',
      } as unknown as any);
      return;
    }

    getMentorDetail(id);
  }, [id]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      scrollToError();
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [errors]);

  function isSameCoachSet(a: string[], b: string[]) {
    if (a.length !== b.length) return false;
    const left = [...a].sort();
    const right = [...b].sort();
    return left.every((value, index) => value === right[index]);
  }

  // `coach_ids` is replace-the-whole-list: omitting it keeps the current
  // coaches, sending [] unassigns them all. Only send it on an actual change.
  function getCoachIdsPayload(values: MentorFormType) {
    const current = ((values.coach_ids as any[]) ?? []).map((option) =>
      String(option.value),
    );
    const initial = initialCoachIds.current;

    if (initial === null) {
      return current.length ? { coach_ids: current.map(Number) } : {};
    }

    if (isSameCoachSet(initial, current)) return {};

    return { coach_ids: current.map(Number) };
  }

  function getPayload() {
    const status = isActive ? 'active' : 'inactive';
    const values = getValues();
    const { gender } = getValueFromSelect(values, ['gender']);
    const dateOfBirth = values.date_of_birth
      ? dayjs(values.date_of_birth.toString()).format('YYYY-MM-DD')
      : undefined;

    return {
      role: ROLE.MENTOR,
      status,
      email: values.email,
      password: values.password || undefined,
      password_confirmation: values.password_confirmation || undefined,
      require_password_update: values.require_password_update,
      phone_number: values.phone_number,
      profile_image_id: values.profile_image_id,
      first_name_kanji: values.first_name_kanji,
      last_name_kanji: values.last_name_kanji,
      first_name: values.first_name,
      last_name: values.last_name,
      date_of_birth: dateOfBirth,
      gender,
      post_code: values.post_code,
      address: values.address,
      ...getCoachIdsPayload(values),
    };
  }

  function handleError(error: any) {
    const { status_code, errors: errorList } = error || {};

    if (status_code === HTTP_STATUS.FORM_ERROR && Array.isArray(errorList)) {
      errorList.forEach((item: any) => {
        const { field, message, error_code } = item;
        const isAssignCoachError =
          error_code === ASSIGN_COACH_ERROR_CODE.COACH_IDS_INVALID ||
          error_code === ASSIGN_COACH_ERROR_CODE.COACH_ALREADY_HAS_MENTOR;
        const targetField = isAssignCoachError ? 'coach_ids' : field;

        if (!targetField) return;
        setError(targetField, {
          message:
            message ||
            (error_code === ASSIGN_COACH_ERROR_CODE.COACH_ALREADY_HAS_MENTOR
              ? t('user.assign_coach_error.coach_already_has_mentor')
              : t('user.assign_coach_error.coach_ids_invalid')),
        });
      });
      return;
    }

    toast?.current?.show(toastErrorMessage(t('user.mentor_fail')));
  }

  async function onSubmit() {
    setLoadingGlobal(true);
    const payload = getPayload();

    const { data, error } = id
      ? await apiService.users.update(id, payload)
      : await apiService.users.create(payload);

    setLoadingGlobal(false);

    if (error) {
      // `coach_ids` is all-or-nothing: nothing was saved, so keep whatever the
      // user has on screen instead of reloading the form.
      handleError(error);
      return;
    }

    if (data) {
      toast?.current?.show(
        toastMessage(
          id ? t('user.update_mentor_success') : t('user.mentor_success'),
        ),
      );
      navigate(PATH.mentor_management);
    }
  }

  const onBeforeUploadAvatar = (event: any) => {
    const MAX_SIZE_MB = 20;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

    const file = event.files[0];
    if (file.size > MAX_SIZE_BYTES) {
      event.options.clear();
      toast?.current?.show(toastErrorMessage(t('common.file.image_max_20MB')));
      return;
    }
  };

  function handleNavigateBack() {
    navigate(PATH.mentor_management);
  }

  return (
    <FormProvider {...formMethods}>
      <form
        className="mt-[16px]"
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
      >
        <div className="form-item flex">
          <div className="label w-56 flex">
            <span>{t('user.status')}</span>
          </div>
          <InputSwitch
            name="status"
            checked={isActive}
            onChange={(e) => setIsActive(e.value)}
          />
          <span className="ml-2">
            {isActive ? t('user.active') : t('user.in_active')}
          </span>
        </div>

        <div className="form-item flex mt-[20px]">
          <div className="label w-56 flex">
            <span>{t('user.avatar')}</span>
          </div>
          <div className="w-[512px] flex">
            <UploadImage
              idChooseBtn="choose-image"
              className="relative w-[366px] h-[126px] flex-full-center border border-gray-200"
              name="profile_image_id"
              accept=".svg,.jpg,.jpeg,.png"
              defaultUrl={defaultImage}
              onBeforeUpload={onBeforeUploadAvatar}
              maxSize={5}
              handleUploaded={(imageId) => {
                setValue('profile_image_id', imageId);
              }}
              textUpload="ファイルを選択"
              textSubUpload="またはここにファイルをドロップ"
            />
          </div>
        </div>

        <div className="form-item flex mt-[20px]">
          <div className="label w-56 flex">
            <span>{t('user.email')}</span>
            <span className="text-error-500">*</span>
          </div>
          <InputTextCustomForm
            name="email"
            wrapClass="w-[512px]"
            disabled={!!id}
          />
        </div>

        <div className="form-item flex mt-[20px]">
          <div className="label w-56 flex">
            <span>{t('user.phone_number')}</span>
            <span className="text-error-500">*</span>
          </div>
          <div className="w-[512px]">
            <InputTextCustomForm
              name="phone_number"
              wrapClass="w-full"
              maxLength={MAX_LENGTH.PHONE_NUMBER}
              isNumber
            />
            <p className="text-[#757575] text-[12px] mt-2 whitespace-pre-line">
              {t('user.phone_hint')}
            </p>
          </div>
        </div>

        <div className="form-item flex mt-[20px]">
          <div className="label w-56 flex">
            <span>{t('user.password')}</span>
            {!id && <span className="text-error-500">*</span>}
          </div>
          <div className="w-[512px]">
            <div className="grid grid-cols-2 gap-6">
              <InputTextCustomForm
                name="password"
                type="text"
                isHideValue={true}
                placeholder={t('user.password')}
                maxLength={MAX_LENGTH.PASSWORD}
              />
              <InputTextCustomForm
                name="password_confirmation"
                type="text"
                isHideValue={true}
                placeholder={t('user.password_confirm')}
                maxLength={MAX_LENGTH.PASSWORD}
              />
            </div>
            <p className="text-[#757575] text-[12px] mt-2 whitespace-pre-line">
              {t('user.password_hint')}
            </p>
          </div>
        </div>

        <div className="break-line border-t border-gray-200 h-[1px] mt-[20px]"></div>

        <div className="form-item flex mt-[20px]">
          <div className="label w-56 flex">
            <span>{t('user.kanji_name')}</span>
            <span className="text-error-500">*</span>
          </div>
          <div className="w-[512px] grid grid-cols-2 gap-6">
            <InputTextCustomForm
              name="last_name_kanji"
              placeholder={t('user.last_name_kanji_placeholder')}
            />
            <InputTextCustomForm
              name="first_name_kanji"
              placeholder={t('user.first_name_kanji_placeholder')}
            />
          </div>
        </div>

        <div className="form-item flex mt-[20px]">
          <div className="label w-56 flex">
            <span>{t('user.name')}</span>
            <span className="text-error-500">*</span>
          </div>
          <div className="w-[512px] grid grid-cols-2 gap-6">
            <InputTextCustomForm
              name="last_name"
              placeholder={t('user.last_name_placeholder')}
            />
            <InputTextCustomForm
              name="first_name"
              placeholder={t('user.first_name_placeholder')}
            />
          </div>
        </div>

        {/* 担当コーチ: the coaches this mentor supervises (1 mentor : n coaches). */}
        <div className="form-item flex mt-[20px]">
          <div className="label w-56 flex">
            <span>{t('user.assigned_coaches')}</span>
          </div>
          <div className="w-[512px]">
            <InfinitySelect
              name="coach_ids"
              placeholder=""
              wrapClass="w-[512px]"
              isMulti
              service={canPickCoach && fetchCoachOptions}
              defaultOption={assignedCoachOptions}
              fieldLabels={{
                label: 'full_name_kanji',
                value: 'id',
                subLabel: 'email',
              }}
              filterOption={() => true}
              components={{ MultiValueLabel: CoachMultiValueLabel, Option: CoachOption }}
            />
          </div>
        </div>

        <div className="form-item flex mt-[20px]">
          <div className="label w-56 flex">
            <span>{t('user.date_of_birth')}</span>
          </div>
          <div className="w-[512px]">
            <CalendarCustomForm
              wrapClass="w-full"
              name="date_of_birth"
              dateFormat="yy/mm/dd"
              maxDate={new Date()}
              showIcon
              icon={() => <i className="pi pi-calendar text-[18px]" />}
              placeholder="yyyy/mm/dd"
              panelClassName="calendar-date-of-birth"
            />
          </div>
        </div>

        <div className="form-item flex mt-[20px]">
          <div className="label w-56 flex">
            <span>{t('user.gender')}</span>
          </div>
          <div className="w-[512px]">
            <InfinitySelect
              name="gender"
              placeholder=""
              wrapClass="w-[512px]"
              isClearable={false}
              defaultOption={[
                { value: 'male', label: t('common.gender.male') },
                { value: 'female', label: t('common.gender.female') },
                { value: 'other', label: t('common.gender.other') },
              ]}
            />
          </div>
        </div>

        <div className="form-item flex mt-[20px]">
          <div className="label w-56 flex">
            <span>{t('user.post_code')}</span>
            <span className="text-error-500">*</span>
          </div>
          <div className="w-[512px]">
            <InputTextCustomForm
              wrapClass="w-full"
              name="post_code"
              placeholder="XXXXXXX"
              maxLength={MAX_LENGTH.POSTCODE}
            />
            <p className="text-[#757575] text-[12px] mt-2 whitespace-pre-line">
              {t('user.postcode_hint')}
            </p>
          </div>
        </div>

        <div className="form-item flex mt-[20px]">
          <div className="label w-56 flex">
            <span>{t('user.address')}</span>
            <span className="text-error-500">*</span>
          </div>
          <TextareaCustomForm
            name="address"
            wrapClass="w-[512px]"
            maxLength={MAX_LENGTH.ADDRESS}
          />
        </div>

        <div className="action flex justify-end mt-[25px] border-t border-gray-200 py-[18px]">
          <ButtonCustom
            className="w-[140px]"
            type="button"
            severity="secondary"
            onClick={() => handleNavigateBack()}
          >
            {t('common.form.cancel')}
          </ButtonCustom>
          <ButtonCustom
            className="ml-2 w-[140px]"
            type="submit"
            loading={loadingGlobal}
          >
            {id ? t('common.form.save') : t('common.form.new')}
          </ButtonCustom>
        </div>
      </form>
    </FormProvider>
  );
};

export default MentorForm;
